
import { useState } from "react";
import { Link } from "react-router-dom";
import { PinInfo } from "@/components/pins/PinInfo";
import { PinComments } from "@/components/pins/PinComments";
import { useEffect } from "react";
import { preloadCriticalImages } from "@/utils/image-optimization";
import { Button } from "@/components/ui/button";
import { Maximize2, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SaveToCollection } from "@/components/SaveToCollection";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { HeartButton } from "@/components/ui/heart-button";
import { ShareMenu } from "@/components/ui/ShareMenu";

interface PinDetailsContentProps {
  pin: any;
  user: any;
  likeCount: number;
  commentCount: number;
  userHasLiked: boolean;
  collections: any[];
  comments: any[];
  pinId: string;
  saveToCollection: (collectionId: string) => Promise<void>;
  onLikeToggle: () => Promise<void>;
  onCommentAdded: () => void;
  refreshComments: () => void;
}

export function PinDetailsContent({
  pin,
  user,
  likeCount,
  commentCount,
  userHasLiked,
  collections,
  comments,
  pinId,
  saveToCollection,
  onLikeToggle,
  onCommentAdded,
  refreshComments
}: PinDetailsContentProps) {
  const [showImageModal, setShowImageModal] = useState(false);
  // Change default state to false to hide comments initially
  const [showComments, setShowComments] = useState(false);
  
  // Preload the pin image as it's critical content
  useEffect(() => {
    if (pin?.image_url) {
      preloadCriticalImages([pin.image_url]);
    }
  }, [pin?.image_url]);
    
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-[auto,400px] gap-6 max-w-4xl mx-auto">
        {/* Left column - Image */}
        <div className="relative rounded-lg overflow-hidden bg-black/5 dark:bg-black/20 flex items-center justify-center">
          <div 
            className="pin-image-wrapper relative cursor-pointer w-full"
            onClick={() => setShowImageModal(true)}
          >
            <div className="flex justify-center items-center">
              <img
                src={pin.image_url}
                alt={pin.title || "Pin"}
                className="rounded-lg object-contain max-h-[85vh]"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block'
                }}
                loading="eager"
              />
            </div>
            
            {/* Maximize button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-4 right-4 bg-white/50 hover:bg-white/80 dark:bg-black/50 dark:hover:bg-black/80 backdrop-blur-sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(true);
              }}
              aria-label="Ver em tamanho completo"
            >
              <Maximize2 className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Image modal */}
          <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
            <DialogContent className={cn(
              "max-w-screen-lg w-[95vw] p-1 bg-black/80 border-none",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0"
            )}>
              <div className="relative w-full h-full max-h-[90vh] flex items-center justify-center">
                <img 
                  src={pin.image_url} 
                  alt={pin.title || "Pin em tamanho completo"} 
                  className="max-w-full max-h-[85vh] object-contain" 
                />
                <Button 
                  variant="ghost" 
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                  onClick={() => setShowImageModal(false)}
                  aria-label="Fechar"
                >
                  Fechar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Right column - Info and Comments */}
        <div className="space-y-6 md:px-2">
          {/* Pin info section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to={`/profile/${pin.user_id}`} className="block">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={pin.profile?.avatar_url} />
                    <AvatarFallback>{pin.profile?.username?.charAt(0) || "U"}</AvatarFallback>
                  </Avatar>
                </Link>
                <div>
                  <Link to={`/profile/${pin.user_id}`} className="font-medium hover:underline">
                    {pin.profile?.username || "Usuário"}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {new Date(pin.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {/* Add SaveToCollection component here */}
              {user && <SaveToCollection postId={pinId} />}
            </div>

            <div>
              <h1 className="text-2xl font-bold">{pin.title}</h1>
              <p className="mt-2">{pin.description}</p>
            </div>
            
            <div className="flex items-center gap-4 pt-2">
              <HeartButton 
                initialCount={likeCount} 
                isLiked={userHasLiked} 
                onToggle={onLikeToggle}
              />
              
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => setShowComments(!showComments)}>
                <MessageCircle className="h-4 w-4" /> {commentCount}
              </Button>
              
              {/* Share menu component */}
              <ShareMenu 
                title={pin.title || "Pin de arte incrível"}
                url={`${window.location.origin}/pin/${pinId}`}
                imageUrl={pin.image_url}
              />
            </div>
          </div>
          
          <hr className="border-gray-200" />
          
          {/* Comments section with toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Comentários ({comments.length})</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowComments(!showComments)}
                className="flex items-center gap-1 font-medium"
              >
                {showComments ? (
                  <>
                    <ChevronUp className="h-4 w-4" /> Ocultar
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" /> Mostrar
                  </>
                )}
              </Button>
            </div>
            
            {showComments && (
              <ScrollArea className="h-[400px] rounded-md border p-4">
                <PinComments
                  comments={comments}
                  pinId={pinId}
                  user={user}
                  onCommentAdded={onCommentAdded}
                  refreshComments={refreshComments}
                />
              </ScrollArea>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
